import React from "react";
import { AppButton, Callout } from "../../ui";
import { conflictText, formatDay, labelOf, nounOf } from "./scheduleFormat";

const plural = (n, word) => `${n} ${n === 1 ? word : word === "class" ? "classes" : `${word}s`}`;

/** Whether ScheduleNotice has anything to say — for a caller that wraps it in spacing. */
export const scheduleNeedsNotice = (schedule) =>
  !!schedule && (
    schedule.suggestion?.reason === "behind"
    || schedule.suggestion?.reason === "undated"
    || schedule.conflictCount > 0
    || !!schedule.pastBatchEnd
  );

/**
 * ScheduleNotice — what is wrong with a batch's syllabus dates, and the one
 * repair worth offering. Renders nothing when the schedule is on track.
 *
 * The server decides the repair (`schedule.suggestion`): resume from the first
 * day still to be taught when the batch is behind, else re-plan from the first
 * day sitting on a holiday or a day the batch does not meet.
 *
 * Props:
 *   schedule     — the `schedule` block of GET /syllabus/batch/:id/syllabus
 *   sessions     — that response's sessions, for the suggested day's details
 *   onRepair     — (session, date) => void; opens the reschedule dialog
 *   onEditBatch  — optional; offered when the plan runs past the batch's end
 */
const ScheduleNotice = ({ schedule, sessions = [], onRepair, onEditBatch }) => {
  if (!scheduleNeedsNotice(schedule)) return null;
  const { suggestion, overdueCount, conflictCount, pastBatchEnd } = schedule;
  const target = suggestion ? sessions.find((s) => s.dayNumber === suggestion.fromDay) : null;
  const noun = nounOf(sessions[0]);
  const notices = [];

  if (suggestion?.reason === "behind" && target) {
    notices.push(
      <Callout
        key="behind"
        tone="warning"
        action={onRepair ? (
          <AppButton size="xs" variant="outline" onClick={() => onRepair(target, suggestion.date)}>
            Resume from {formatDay(suggestion.date)}
          </AppButton>
        ) : null}
      >
        <strong>{plural(overdueCount, noun)} behind schedule.</strong>{" "}
        {labelOf(target)} was planned for {formatDay(target.plannedDate)} and has not been taught yet.
        Resuming moves it and every {noun} after it onto the next class days.
      </Callout>,
    );
  }

  if (conflictCount > 0) {
    const first = suggestion?.reason === "conflict" && target
      ? target
      : sessions.find((s) => s.schedule?.conflict);
    notices.push(
      <Callout
        key="conflict"
        tone="warning"
        action={onRepair && suggestion?.reason === "conflict" && target ? (
          <AppButton size="xs" variant="outline" onClick={() => onRepair(target, suggestion.date)}>
            Re-plan from {labelOf(target)}
          </AppButton>
        ) : null}
      >
        <strong>{plural(conflictCount, noun)} planned on a day with no class.</strong>
        {first ? ` ${labelOf(first)} is on ${formatDay(first.plannedDate)} (${conflictText(first.schedule?.conflict)}).` : ""}
      </Callout>,
    );
  }

  if (suggestion?.reason === "undated" && target) {
    const n = schedule.undatedCount || 1;
    notices.push(
      <Callout
        key="undated"
        tone="warning"
        action={onRepair ? (
          <AppButton size="xs" variant="outline" onClick={() => onRepair(target, suggestion.date)}>
            Plan dates from {formatDay(suggestion.date)}
          </AppButton>
        ) : null}
      >
        <strong>{plural(n, noun)} with no date yet.</strong>{" "}
        {labelOf(target)} was added after this batch started. Planning puts it and every {noun} after it on the next class days.
      </Callout>,
    );
  }

  if (pastBatchEnd) {
    notices.push(
      <Callout
        key="end"
        tone="info"
        action={onEditBatch ? (
          <AppButton size="xs" variant="outline" onClick={onEditBatch}>Edit batch</AppButton>
        ) : null}
      >
        The last {noun} is on {formatDay(schedule.lastPlannedDate)}, after the batch ends on{" "}
        {formatDay(schedule.batchEndDate)}.
      </Callout>,
    );
  }

  if (!notices.length) return null;
  return <div className="tw-flex tw-flex-col tw-gap-2">{notices}</div>;
};

export default ScheduleNotice;
